import { Theme } from '@monorepo/relayably/theme'
import { Clock3, PhoneCall, TrendingUp } from 'lucide-react-native'
import React from 'react'
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native'
import { DashboardAgentUsage, DashboardCallSummary, DashboardOverview } from '../types'

const hexToRgba = (hex: string, alpha: number) => {
  const value = hex.replace('#', '')
  const expanded =
    value.length === 3
      ? value
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : value
  const r = parseInt(expanded.slice(0, 2), 16)
  const g = parseInt(expanded.slice(2, 4), 16)
  const b = parseInt(expanded.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const metricIcon = (label: string, color: string) => {
  if (label.toLowerCase().includes('average')) {
    return <Clock3 color={color} size={18} />
  }

  if (label.toLowerCase().includes('week')) {
    return <TrendingUp color={color} size={18} />
  }

  return <PhoneCall color={color} size={18} />
}

const formatDuration = (seconds: number) => {
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m`
  }

  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`
}

export const OverviewScreen = ({
  theme,
  overview,
  onOpenCall,
}: {
  theme: Theme
  overview: DashboardOverview
  onOpenCall: (call: DashboardCallSummary) => void
}) => {
  const styles = createStyles(theme)

  return (
    <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
      <View style={styles.metricGrid}>
        {overview.metrics.map(metric => (
          <View key={metric.label} style={styles.metricCard}>
            <View style={styles.metricIconWrap}>{metricIcon(metric.label, theme.colors.primary)}</View>
            <Text style={styles.metricLabel}>{metric.label}</Text>
            <Text style={styles.metricValue}>{metric.value}</Text>
            <Text style={styles.metricTrend}>{metric.trendLabel}</Text>
          </View>
        ))}
      </View>

      <View style={styles.twoColumn}>
        <View style={styles.panel}>
          <Text style={styles.sectionTitle}>What your calls looked like</Text>
          <Text style={styles.sectionBody}>A quick read on the kinds of calls Relayably has been handling.</Text>
          <View style={styles.callTypeStack}>
            {overview.callTypes.map(type => (
              <View key={type.key} style={styles.callTypeRow}>
                <View style={styles.callTypeLabelRow}>
                  <View style={[styles.colorDot, { backgroundColor: type.color }]} />
                  <Text style={styles.callTypeLabel}>{type.label}</Text>
                </View>
                <View style={styles.callTypeBarTrack}>
                  <View
                    style={[
                      styles.callTypeBarFill,
                      {
                        width: `${Math.max(10, Math.min(100, overview.recentCalls.length ? (type.count / Math.max(overview.recentCalls.length, 1)) * 100 : 10))}%`,
                        backgroundColor: type.color,
                      },
                    ]}
                  />
                </View>
                <Text style={styles.callTypeCount}>{type.count}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.panel}>
          <Text style={styles.sectionTitle}>Which agents are working most</Text>
          <Text style={styles.sectionBody}>See who is taking the most load and for how long.</Text>
          <View style={styles.agentUsageStack}>
            {overview.agentUsage.map((agent: DashboardAgentUsage) => (
              <View key={agent.id} style={styles.agentUsageRow}>
                <View style={styles.agentUsageHeader}>
                  <Text style={styles.agentName}>{agent.name}</Text>
                  <Text style={styles.agentMeta}>
                    {agent.calls} calls • {formatDuration(agent.totalDurationSeconds)}
                  </Text>
                </View>
                <View style={styles.agentUsageTrack}>
                  <View style={[styles.agentUsageFill, { width: `${Math.max(agent.usagePercent, agent.calls ? 12 : 4)}%` }]} />
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View style={styles.panel}>
        <Text style={styles.sectionTitle}>Recent calls</Text>
        <Text style={styles.sectionBody}>Jump straight into the conversations that mattered most this week.</Text>
        <View style={styles.recentCallList}>
          {overview.recentCalls.map(call => (
            <Pressable key={call.id} onPress={() => onOpenCall(call)} style={styles.callCard}>
              <View style={styles.callCardTop}>
                <Text style={styles.callPhone}>{call.phoneNumber}</Text>
                <View style={styles.callBadge}>
                  <Text style={styles.callBadgeText}>{call.categoryLabel}</Text>
                </View>
              </View>
              <Text style={styles.callSummary}>{call.summary || 'Open to read the full transcript and summary.'}</Text>
              <Text style={styles.callMeta}>
                {call.agentName} • {new Date(call.startedAt).toLocaleDateString()} • {formatDuration(call.durationSeconds)}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>
    </ScrollView>
  )
}

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    content: {
      gap: 20,
      paddingBottom: 20,
    },
    metricGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 16,
    },
    metricCard: {
      minWidth: 220,
      flexGrow: 1,
      flexBasis: 220,
      borderRadius: 24,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.1),
      gap: 10,
    },
    metricIconWrap: {
      width: 36,
      height: 36,
      borderRadius: 18,
      backgroundColor: hexToRgba(theme.colors.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
    },
    metricLabel: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 1.2,
      textTransform: 'uppercase',
    },
    metricValue: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 30,
      lineHeight: 34,
    },
    metricTrend: {
      color: theme.colors.primary,
      fontSize: 13,
      fontWeight: '600',
    },
    twoColumn: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 16,
    },
    panel: {
      flexGrow: 1,
      flexBasis: 320,
      borderRadius: 24,
      padding: 20,
      backgroundColor: hexToRgba(theme.colors.surface, 0.96),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.1),
      gap: 16,
    },
    sectionTitle: {
      color: theme.colors.text,
      fontFamily: 'Georgia',
      fontSize: 26,
      lineHeight: 30,
    },
    sectionBody: {
      color: theme.colors.inactive,
      fontSize: 14,
      lineHeight: 22,
    },
    callTypeStack: {
      gap: 14,
    },
    callTypeRow: {
      gap: 8,
    },
    callTypeLabelRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    colorDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
    },
    callTypeLabel: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '600',
    },
    callTypeBarTrack: {
      height: 10,
      borderRadius: 999,
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      overflow: 'hidden',
    },
    callTypeBarFill: {
      height: '100%',
      borderRadius: 999,
    },
    callTypeCount: {
      color: theme.colors.inactive,
      fontSize: 12,
      fontWeight: '600',
    },
    agentUsageStack: {
      gap: 14,
    },
    agentUsageRow: {
      gap: 8,
    },
    agentUsageHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
    },
    agentName: {
      color: theme.colors.text,
      fontSize: 14,
      fontWeight: '700',
      flex: 1,
    },
    agentMeta: {
      color: theme.colors.inactive,
      fontSize: 12,
    },
    agentUsageTrack: {
      height: 10,
      borderRadius: 999,
      backgroundColor: hexToRgba(theme.colors.primary, 0.08),
      overflow: 'hidden',
    },
    agentUsageFill: {
      height: '100%',
      borderRadius: 999,
      backgroundColor: theme.colors.primary,
    },
    recentCallList: {
      gap: 12,
    },
    callCard: {
      borderRadius: 18,
      padding: 16,
      backgroundColor: hexToRgba(theme.colors.background, 0.7),
      borderWidth: 1,
      borderColor: hexToRgba(theme.colors.primary, 0.08),
      gap: 10,
    },
    callCardTop: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 12,
    },
    callPhone: {
      color: theme.colors.text,
      fontSize: 16,
      fontWeight: '700',
      flex: 1,
    },
    callBadge: {
      borderRadius: 999,
      paddingHorizontal: 10,
      paddingVertical: 5,
      backgroundColor: hexToRgba(theme.colors.primary, 0.1),
    },
    callBadgeText: {
      color: theme.colors.primary,
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
    },
    callSummary: {
      color: theme.colors.text,
      fontSize: 14,
      lineHeight: 22,
    },
    callMeta: {
      color: theme.colors.inactive,
      fontSize: 12,
      lineHeight: 18,
    },
  })
